import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { LOGO_ICON_BLUE } from '@/components/SplashScreen';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <img src={LOGO_ICON_BLUE} alt="Rebuilt" className="h-8 w-8" />
          <h1 className="text-xl font-bold text-gray-900">Privacy Policy</h1>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="h-8 w-8 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">Privacy Policy</h2>
          </div>

          <p className="text-sm text-gray-500 mb-8">
            <strong>Last Updated:</strong> January 1, 2025
          </p>

          <div className="prose prose-gray max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h3>
              <p className="text-gray-700 leading-relaxed">
                At Rebuilt, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our EMI assistance platform and related services.
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                By using our Services, you consent to the data practices described in this Privacy Policy. If you do not agree with this policy, please do not use our Services.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h3>
              
              <p className="text-gray-700 font-semibold mb-3">2.1 Personal Information You Provide</p>
              <p className="text-gray-700 leading-relaxed mb-2">
                When you register and use our Services, we collect the following information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Identity Information:</strong> Full name, date of birth, gender, photograph</li>
                <li><strong>Contact Information:</strong> Mobile number, email address, residential address</li>
                <li><strong>KYC Documents:</strong> PAN card details, Aadhaar information (via secure offline XML method), address proof</li>
                <li><strong>Financial Information:</strong> Bank account details, income information, employment details, credit history</li>
                <li><strong>Loan Information:</strong> Loan amount, purpose, tenure, EMI details</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3 mt-6">2.2 Information Collected Automatically</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers</li>
                <li><strong>Usage Information:</strong> Pages visited, features used, time spent on platform, click patterns</li>
                <li><strong>Location Information:</strong> IP address, approximate geographic location (with your permission)</li>
                <li><strong>Log Data:</strong> Access times, browser type, referring URLs, error logs</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3 mt-6">2.3 Information from Third Parties</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Credit Bureaus:</strong> Credit scores and credit history reports</li>
                <li><strong>KYC Service Providers:</strong> Verification status and identity confirmation</li>
                <li><strong>Payment Gateways:</strong> Transaction status and payment history</li>
                <li><strong>Fraud Detection Services:</strong> Risk assessment and fraud indicators</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use your information for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Service Delivery:</strong> Process loan applications, verify identity, disburse funds, manage repayments</li>
                <li><strong>Credit Assessment:</strong> Evaluate creditworthiness, determine loan eligibility, set interest rates</li>
                <li><strong>Communication:</strong> Send application updates, payment reminders, promotional offers, customer support responses</li>
                <li><strong>Compliance:</strong> Comply with KYC/AML regulations, prevent fraud, meet legal obligations</li>
                <li><strong>Platform Improvement:</strong> Analyze user behavior, improve features, develop new services</li>
                <li><strong>Security:</strong> Protect against unauthorized access, fraud detection, enforce terms of service</li>
                <li><strong>Marketing:</strong> Send relevant offers, personalized recommendations (with your consent)</li>
              </ul>
            </section>

            {/* Legal Basis for Processing */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Legal Basis for Processing</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We process your personal information based on the following legal grounds:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Contractual Necessity:</strong> Processing is necessary to provide our Services and fulfill our agreement with you</li>
                <li><strong>Legal Obligations:</strong> Processing is required to comply with KYC, AML, and other regulatory requirements</li>
                <li><strong>Legitimate Interests:</strong> Processing is necessary for fraud prevention, risk assessment, and service improvement</li>
                <li><strong>Consent:</strong> Where you have given explicit consent for specific processing activities</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">5. How We Share Your Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We may share your information with the following third parties:
              </p>

              <p className="text-gray-700 font-semibold mb-2">5.1 Lending Partners</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                We share your application information with our lending partners who make final approval and disbursement decisions.
              </p>

              <p className="text-gray-700 font-semibold mb-2">5.2 Service Providers</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>KYC verification providers (e.g., Digio)</li>
                <li>Payment gateway providers (e.g., Razorpay)</li>
                <li>Cloud hosting services</li>
                <li>SMS and email service providers</li>
                <li>Analytics and data processing services</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-2">5.3 Credit Bureaus</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                We share information with credit bureaus to assess your creditworthiness and report repayment behavior.
              </p>

              <p className="text-gray-700 font-semibold mb-2">5.4 Legal and Regulatory Authorities</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                We may disclose information when required by law, court order, or to comply with regulatory requirements.
              </p>

              <p className="text-gray-700 font-semibold mb-2">5.5 Business Transfers</p>
              <p className="text-gray-700 leading-relaxed">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">6. Data Security</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Encryption:</strong> Data transmission is encrypted using SSL/TLS protocols</li>
                <li><strong>Access Controls:</strong> Strict role-based access to sensitive information</li>
                <li><strong>Authentication:</strong> Multi-factor authentication for account access</li>
                <li><strong>Monitoring:</strong> Continuous monitoring for security threats and unauthorized access</li>
                <li><strong>Regular Audits:</strong> Periodic security assessments and vulnerability testing</li>
                <li><strong>Secure Storage:</strong> Data stored in secure, access-controlled servers</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                While we take reasonable measures to protect your information, no system is completely secure. You are responsible for maintaining the confidentiality of your account credentials.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">7. Data Retention</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We retain your personal information for as long as necessary to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide our Services and maintain your account</li>
                <li>Comply with legal and regulatory obligations (typically 7-10 years for financial records)</li>
                <li>Resolve disputes and enforce our agreements</li>
                <li>Prevent fraud and maintain security</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                After the retention period expires, we securely delete or anonymize your information unless longer retention is required by law.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">8. Your Privacy Rights</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your information (subject to legal retention requirements)</li>
                <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                <li><strong>Portability:</strong> Receive your information in a structured, commonly used format</li>
                <li><strong>Withdrawal of Consent:</strong> Withdraw consent for marketing communications</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                To exercise these rights, please contact us at privacy@rebuilt.in. We will respond within 30 days of your request.
              </p>
            </section>

            {/* Cookies and Tracking */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">9. Cookies and Tracking Technologies</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Remember your preferences and settings</li>
                <li>Maintain your login session</li>
                <li>Analyze platform usage and performance</li>
                <li>Deliver personalized content and advertisements</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                You can control cookies through your browser settings. However, disabling cookies may affect the functionality of our Services.
              </p>
            </section>

            {/* Third-Party Links */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">10. Third-Party Links</h3>
              <p className="text-gray-700 leading-relaxed">
                Our Services may contain links to third-party websites and services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any personal information.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">11. Children's Privacy</h3>
              <p className="text-gray-700 leading-relaxed">
                Our Services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete it immediately.
              </p>
            </section>

            {/* International Transfers */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">12. International Data Transfers</h3>
              <p className="text-gray-700 leading-relaxed">
                Your information is primarily stored and processed in India. If we transfer data outside India, we ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">13. Changes to This Privacy Policy</h3>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our platform and updating the "Last Updated" date. We may also send you email notifications for significant changes. Your continued use of our Services after such changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">14. Contact Us</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-800"><strong>Data Protection Officer</strong></p>
                <p className="text-gray-800"><strong>Email:</strong> privacy@rebuilt.in</p>
                <p className="text-gray-800"><strong>Phone:</strong> +91-XXXX-XXXXXX</p>
                <p className="text-gray-800"><strong>Address:</strong> [Your registered business address]</p>
              </div>
            </section>

            {/* Grievance Officer */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">15. Grievance Officer</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                In accordance with Information Technology Act 2000 and rules made thereunder, the name and contact details of the Grievance Officer are provided below:
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-800"><strong>Name:</strong> [Grievance Officer Name]</p>
                <p className="text-gray-800"><strong>Email:</strong> grievance@rebuilt.in</p>
                <p className="text-gray-800"><strong>Phone:</strong> +91-XXXX-XXXXXX</p>
                <p className="text-gray-800"><strong>Time:</strong> Monday to Friday (10:00 AM - 6:00 PM IST)</p>
              </div>
            </section>
          </div>

          {/* Back to Home Button */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
